import {
  ScheduleData,
  ScheduleQueryParams,
  dayMonthDateString,
  fetchSchedule,
  shiftTimeRangeDurationString,
} from '@nextforce/api'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { t } from 'app/i18l'
import { useServer } from 'app/server/context'
import { BaseHomeItem, SummaryItem } from 'home/types'
import {
  makeErrorHomeItem,
  makeSuccessHomeItem,
} from 'home/update_app/shared/utils'
import moment from 'moment'
import { MINS_TO_MS } from 'shared/constants'
import { RetryQuery } from 'shared/queries/utils'
import { generateUniqueId } from 'shared/utils'
import { getShiftEndDateTime } from 'shifts/utils'
import { useStores } from 'stores/queries/list'
import { useUser } from 'users/queries/detail'
import keys from './shared/keys'

export const helperScheduleQueryParams: ScheduleQueryParams = {
  schedule: { calendar: 'helper' },
}

export function useSchedule<T = ScheduleData>(
  params: ScheduleQueryParams | undefined,
  getId: () => string,
  enabled = true,
  select?: (data: ScheduleData) => T,
  retryQuery?: RetryQuery
) {
  const { server } = useServer()
  const { data: stores } = useStores(data => data.stores)
  const { data: user } = useUser()

  return useQuery<ScheduleData, unknown, T>({
    queryKey: params ? keys.list(params) : [],
    queryFn: fetch,
    enabled: !!params && !!user && !!stores && enabled,
    refetchInterval: 15 * MINS_TO_MS,
    placeholderData:
      params && 'pageUri' in params ? keepPreviousData : undefined,
    select,
    retry: retryQuery ?? false,
  })

  function fetch() {
    return fetchSchedule(server, params, stores, getId)
  }
}

export function useHelperScheduleHomeItem() {
  const { data: user } = useUser()
  const { data: hasShifts } = useStores(data => data.hasShifts)
  const enabled = user?.permissions?.canSeeHelperSchedule || hasShifts
  const {
    data: helperSchedule,
    isError: isHelperScheduleError,
    error: helperScheduleError,
  } = useSchedule(helperScheduleQueryParams, generateUniqueId, enabled)

  if (!enabled) {
    return
  }

  const baseItem = {
    title: t('myShifts'),
    route: { name: 'schedule' },
    color: 'black',
    backgroundColor: '#F8D6D4',
    params: { schedule: { calendar: 'helper' } },
  } as BaseHomeItem

  if (isHelperScheduleError) {
    return makeErrorHomeItem(baseItem, helperScheduleError)
  }

  if (helperSchedule) {
    const shifts = Object.values(helperSchedule.events)
      .flat()
      .flatMap(event => (event.type === 'shifts' ? event.shifts : []))
      .filter(shift => {
        return moment(getShiftEndDateTime(shift)).isAfter()
      })

    const summaryItems: SummaryItem[] = shifts.map(shift => ({
      title: `${dayMonthDateString(
        shift.date
      )} • ${shiftTimeRangeDurationString(shift)}`,
      description: `${shift.position ?? t('anyPosition')}\n${
        shift.store.name
      } (${shift.store.storeNumber})`,
    }))

    return makeSuccessHomeItem(baseItem, summaryItems)
  }
}
